import { Typography } from '@vartanainc/design-system';
import { FormikProps, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Ref, useEffect, useImperativeHandle, useMemo } from 'react';
import CompactDropdownInput from '../../../../designSystem/CompactFields/CompactDropDown';
import { WarningAlertBox } from '../../../../components/AlertBox';
import { TAB_NAMES_MAP } from '../../../CommonWidgetV2/commonWidget.constants';

export interface PurchaseInfoFormValues {
  productType: string;
  isHalfHardware: string;
}

interface PurchaseQuestion {
  id: string;
  label: string;
  type: string;
  options: string[];
}

interface PurchaseInformationFormDetails {
  sectionTitle: string;
  questions: PurchaseQuestion[];
}

export interface PurchaseInformationFormRef {
  formRef: FormikProps<PurchaseInfoFormValues>;
  submitPurchaseInfoForm: () => void;
}

interface PurchaseInformationFormProps {
  purchaseInfoFormRef: Ref<PurchaseInformationFormRef> | undefined;
  purchaseInformationFormDetails: PurchaseInformationFormDetails;
  setCanSubmit: (canSubmit: boolean) => void;
  setActive: (active: string) => void;
}

export function PurchaseInformationForm({
  purchaseInfoFormRef,
  purchaseInformationFormDetails,
  setCanSubmit,
  setActive,
}: PurchaseInformationFormProps): JSX.Element {
  const initialValues = useMemo(() => {
    return {
      productType: '',
      isHalfHardware: '',
    };
  }, []);

  // adding this because submission logic is handled via imperative handle
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleSubmit = (values): void => {};

  const validationSchema = Yup.object().shape({
    productType: Yup.string().notOneOf(['other'], 'Product type cannot be "other"'),
    isHalfHardware: Yup.string().notOneOf(['no'], 'Hardware type cannot be "no"'),
  });

  const formikBag = useFormik<PurchaseInfoFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
  });

  useImperativeHandle(purchaseInfoFormRef, () => ({
    formRef: formikBag,
    submitPurchaseInfoForm: formikBag.submitForm,
  }));

  const showWarning = Object.keys(formikBag.errors).length > 0;

  useEffect(() => {
    setActive(TAB_NAMES_MAP.fmv);
  }, [setActive]);

  useEffect(() => {
    const areDropdownsEmpty =
      formikBag.values.productType === '' || formikBag.values.isHalfHardware === '';
    setCanSubmit(!showWarning && !areDropdownsEmpty);
  }, [
    formikBag.values.isHalfHardware,
    formikBag.values.productType,
    setCanSubmit,
    showWarning,
  ]);

  return (
    <div className="flex gap-4 flex-col items-start">
      <Typography variant="heading16" bold>
        {purchaseInformationFormDetails?.sectionTitle}
      </Typography>

      <FormikProvider value={formikBag}>
        <form className="w-full flex gap-4 flex-col" onSubmit={formikBag.handleSubmit}>
          {showWarning && (
            <WarningAlertBox message="Contact your Vartana rep for assistance." />
          )}

          {purchaseInformationFormDetails?.questions.map((question) => {
            const fieldError =
              formikBag.touched[question.id] && formikBag.errors[question.id];
            return (
              <div key={question.id}>
                <CompactDropdownInput
                  name={question.id}
                  label={question.label}
                  options={question.options.map((option) => ({
                    value: option.toLowerCase(),
                    label: option,
                  }))}
                  onChange={(value) => formikBag.setFieldValue(question.id, value.value)}
                  showError={fieldError}
                  showHoverShadow={false}
                />
              </div>
            );
          })}
        </form>
      </FormikProvider>
    </div>
  );
}
